import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { IconColorPicker } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import {
	isTransparentColor,
	isValidHex,
	normalizeHex,
	TRANSPARENT_COLOR,
} from '../../utils/inspectorColors';
import { scrollParentRect } from '../../utils/scrollParent';

const POPOVER_HEIGHT = 48;
const NATIVE_PICKER_DEBOUNCE_MS = 100;

interface Props {
	label: string;
	value: string | number | undefined;
	presetColors: string[];
	onChange: (value: string) => void;
	onCustomColorUsed?: (hex: string) => void;
	allowTransparent?: boolean;
}

const checkerboard = css`
	background-color: var(--mantine-color-white);
	background-image: linear-gradient(45deg, var(--mantine-color-gray-4) 25%, transparent 25%),
		linear-gradient(-45deg, var(--mantine-color-gray-4) 25%, transparent 25%),
		linear-gradient(45deg, transparent 75%, var(--mantine-color-gray-4) 75%),
		linear-gradient(-45deg, transparent 75%, var(--mantine-color-gray-4) 75%);
	background-size: 0.5rem 0.5rem;
	background-position:
		0 0,
		0 0.25rem,
		0.25rem -0.25rem,
		-0.25rem 0;
`;

const Row = styled.div`
	position: relative;
`;

const Control = styled.div`
	display: flex;
	align-items: center;
	width: 8rem;
	height: 1.75rem;
	flex: none;
	overflow: hidden;
	border: 1px solid var(--mantine-color-gray-4);
	border-radius: var(--mantine-radius-sm);
	transition: border-color 100ms ease-out;

	&:focus-within {
		border-color: var(--mantine-color-blue-6);
	}
`;

const SwatchButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 1.75rem;
	height: 100%;
	flex-shrink: 0;
	border: none;
	border-right: 1px solid var(--mantine-color-gray-4);
	background: transparent;
	cursor: pointer;

	&:hover {
		background-color: var(--mantine-color-gray-0);
	}
`;

const Swatch = styled.span<{ $transparent: boolean }>`
	width: 0.875rem;
	height: 0.875rem;
	flex-shrink: 0;
	border: 1px solid var(--mantine-color-gray-4);
	border-radius: 2px;
	${({ $transparent }) => $transparent && checkerboard}
`;

const HexInput = styled.input`
	flex: 1;
	min-width: 0;
	height: 100%;
	padding: 0 0.5rem;
	border: none;
	background: transparent;
	color: var(--mantine-color-text);
	font-family: inherit;
	font-size: 0.75rem;
	text-transform: uppercase;
	outline: none;
`;

const Popover = styled.div<{ $above: boolean }>`
	position: absolute;
	${({ $above }) => ($above ? 'bottom: 100%;' : 'top: 100%;')}
	right: 0;
	z-index: 30;
	display: flex;
	align-items: center;
	gap: 0.25rem;
	${({ $above }) => ($above ? 'margin-bottom: 0.25rem;' : 'margin-top: 0.25rem;')}
	padding: 0.5rem;
	border: 1px solid var(--mantine-color-gray-3);
	border-radius: var(--mantine-radius-md);
	background-color: var(--mantine-color-white);
	box-shadow:
		0 4px 12px -2px rgba(20, 24, 26, 0.12),
		0 2px 4px -2px rgba(20, 24, 26, 0.08);
`;

const PresetSwatch = styled.button<{ $selected: boolean; $transparent?: boolean }>`
	width: 1.5rem;
	height: 1.5rem;
	flex-shrink: 0;
	cursor: pointer;
	border: 1px solid
		${({ $selected }) =>
			$selected ? 'var(--mantine-color-blue-6)' : 'var(--mantine-color-gray-4)'};
	border-radius: var(--mantine-radius-sm);
	box-shadow: ${({ $selected }) =>
		$selected ? '0 0 0 2px var(--mantine-color-blue-1)' : 'none'};
	${({ $transparent }) => $transparent && checkerboard}

	&:hover {
		border-color: var(--mantine-color-gray-9);
	}
`;

const CustomSwatch = styled.span`
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 1.5rem;
	height: 1.5rem;
	flex-shrink: 0;
	border: 1px dashed var(--mantine-color-gray-4);
	border-radius: var(--mantine-radius-sm);
	color: var(--mantine-color-gray-9);

	&:hover {
		border-color: var(--mantine-color-gray-9);
	}

	input {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		opacity: 0;
		cursor: pointer;
	}
`;

export const ColorSwatchRow = ({
	label,
	value,
	presetColors,
	onChange,
	onCustomColorUsed,
	allowTransparent = false,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const hex = normalizeHex(value);
	const transparent = isTransparentColor(value);
	const [opened, setOpened] = useState(false);
	const [above, setAbove] = useState(false);
	const [draft, setDraft] = useState(() =>
		isTransparentColor(value) ? '' : hex.replace('#', '').toUpperCase()
	);
	const [pickerDraft, setPickerDraft] = useState(hex);
	const containerRef = useRef<HTMLDivElement>(null);
	const nativeInputRef = useRef<HTMLInputElement>(null);
	const pickerTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const onChangeRef = useRef(onChange);
	const onCustomColorUsedRef = useRef(onCustomColorUsed);
	onChangeRef.current = onChange;
	onCustomColorUsedRef.current = onCustomColorUsed;

	useEffect(() => {
		setDraft(transparent ? '' : hex.replace('#', '').toUpperCase());
		setPickerDraft(hex);
	}, [hex, transparent]);

	// The native picker fires `input` while dragging; `change` fires once, on close.
	useEffect(() => {
		const element = nativeInputRef.current;
		if (!element) {
			return;
		}
		const handleCommit = (event: Event) => {
			const target = event.target;
			if (target instanceof HTMLInputElement) {
				if (pickerTimeoutRef.current) {
					clearTimeout(pickerTimeoutRef.current);
					pickerTimeoutRef.current = null;
				}
				onChangeRef.current(target.value);
				onCustomColorUsedRef.current?.(target.value);
			}
			setOpened(false);
		};
		element.addEventListener('change', handleCommit);
		return () => element.removeEventListener('change', handleCommit);
	}, [opened]);

	useEffect(
		() => () => {
			if (pickerTimeoutRef.current) {
				clearTimeout(pickerTimeoutRef.current);
			}
		},
		[]
	);

	useEffect(() => {
		if (!opened) {
			return;
		}
		const container = containerRef.current;
		if (container) {
			const bounds = scrollParentRect(container);
			const room = bounds.bottom - container.getBoundingClientRect().bottom;
			setAbove(room < POPOVER_HEIGHT);
		}
	}, [opened]);

	useEffect(() => {
		if (!opened) {
			return;
		}
		const handlePointerDown = (event: PointerEvent) => {
			if (
				event.target instanceof Node &&
				containerRef.current &&
				!containerRef.current.contains(event.target)
			) {
				setOpened(false);
			}
		};
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setOpened(false);
			}
		};
		document.addEventListener('pointerdown', handlePointerDown);
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('pointerdown', handlePointerDown);
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [opened]);

	const commitDraft = () => {
		if (!isValidHex(draft)) {
			setDraft(transparent ? '' : hex.replace('#', '').toUpperCase());
			return;
		}
		const nextHex = `#${draft.replace('#', '').toLowerCase()}`;
		onChange(nextHex);
		onCustomColorUsed?.(nextHex);
	};

	return (
		<Row data-re-inspector-prop-row="" ref={containerRef}>
			<label data-re-inspector-label="">{label}</label>
			<Control>
				<SwatchButton
					type="button"
					aria-expanded={opened}
					aria-label={tMipManage('email_templates.editor.inspector.color_swatches', {
						defaultValue: '{{label}} swatches',
						label,
					})}
					onClick={() => setOpened((previous) => !previous)}
				>
					<Swatch
						$transparent={transparent}
						style={transparent ? undefined : { backgroundColor: hex }}
					/>
				</SwatchButton>
				<HexInput
					type="text"
					value={draft}
					placeholder={
						transparent
							? tMipManage('email_templates.editor.inspector.color_transparent', {
									defaultValue: 'Transparent',
								})
							: undefined
					}
					aria-label={tMipManage('email_templates.editor.inspector.color_hex', {
						defaultValue: '{{label}} hex value',
						label,
					})}
					onChange={(event) => setDraft(event.currentTarget.value)}
					onBlur={commitDraft}
					onKeyDown={(event) => {
						if (event.key === 'Enter') {
							commitDraft();
							event.currentTarget.blur();
						}
					}}
				/>
			</Control>
			{opened && (
				<Popover $above={above} role="group" aria-label={label}>
					{allowTransparent && (
						<PresetSwatch
							type="button"
							$transparent
							$selected={transparent}
							aria-pressed={transparent}
							aria-label={tMipManage(
								'email_templates.editor.inspector.color_transparent',
								{ defaultValue: 'Transparent' }
							)}
							onClick={() => {
								onChange(TRANSPARENT_COLOR);
								setOpened(false);
							}}
						/>
					)}
					{presetColors.map((color) => (
						<PresetSwatch
							key={color}
							type="button"
							aria-label={color}
							aria-pressed={!transparent && hex === color.toLowerCase()}
							$selected={!transparent && hex === color.toLowerCase()}
							style={{ backgroundColor: color }}
							onClick={() => {
								onChange(color);
								setOpened(false);
							}}
						/>
					))}
					<CustomSwatch>
						<IconColorPicker size={12} />
						<input
							ref={nativeInputRef}
							type="color"
							value={pickerDraft}
							aria-label={tMipManage(
								'email_templates.editor.inspector.color_custom',
								{
									defaultValue: 'Custom colour',
								}
							)}
							onChange={(event) => {
								const nextColor = event.currentTarget.value;
								setPickerDraft(nextColor);
								if (pickerTimeoutRef.current) {
									clearTimeout(pickerTimeoutRef.current);
								}
								pickerTimeoutRef.current = setTimeout(() => {
									onChangeRef.current(nextColor);
									pickerTimeoutRef.current = null;
								}, NATIVE_PICKER_DEBOUNCE_MS);
							}}
						/>
					</CustomSwatch>
				</Popover>
			)}
		</Row>
	);
};
